import { FONT_RENDERING_SIZE } from '#base/fonts.js';
import { fabric } from 'fabric';
import { DEFAULT_COLORS, MINIMUM_BITMAP_DPI } from '#base/constants.js';
import { drawPerspective } from '#base/drawPerspective.js';
import { urlForCanvasElement } from '#base/urlForElement.js';
import {
  createCanvas,
  loadImage,
  CanvasRenderingContext2D as NodeCanvasRenderingContext2D,
  Image as NodeImage,
} from 'canvas';
import type { Canvas as NodeCanvas } from 'canvas';
import sha256 from 'crypto-js/sha256.js';

const ptToMM = 25.4 / 72.0; // 1 pt = 1/72 in.

export type GenericCanvas = NodeCanvas | HTMLCanvasElement;
export type GetCanvasFn = (id: string) => GenericCanvas;
type GenericCanvasCtx = CanvasRenderingContext2D | NodeCanvasRenderingContext2D;
function isNodeCanvasRenderingContext2D(ctx: GenericCanvasCtx): ctx is NodeCanvasRenderingContext2D {
  return Object.prototype.hasOwnProperty.call(ctx, 'quality');
}

export type GenericImage = HTMLOrSVGImageElement | NodeImage;
export type GetImageFn = (src?: string) => Promise<GenericImage>;
type LoadImageFn = (src?: string, minimumSize?: MinimumSize) => Promise<GenericImage>;
function isSVGImageElement(img: GenericImage): img is SVGImageElement {
  return typeof img.width !== 'number';
}
function isNodeImage(img: GenericImage): img is NodeImage {
  return Object.prototype.hasOwnProperty.call(img, 'dataMode');
}

export type MinimumSize = {
  width: number;
  height: number;
};

export type EnhancedFabricImage = fabric.Image & {
  bitmapInfo?: any;
  _mask?: fabric.Group;
  _isPremium?: boolean;
  _isFixed?: boolean;
  _elementId?: any;
  _face?: any;
  _w?: number;
  _h?: number;
  _s?: number;
  _i?: number;
};

type ImageCacheValue = {
  image: GenericImage;
  url: string;
};

// Simple image cache to avoid the expense of data-url -> image conversions.
// This speeds up rendering by 10x, but most of the increase comes from
// caching the engraving mask.
class ImageCache {
  _itemId: number;
  _cache: Map<string, ImageCacheValue>;

  constructor(itemId: number) {
    this._itemId = itemId;
    this._cache = new Map();
  }

  get itemId() {
    return this._itemId;
  }

  async loadImage(key: string, url: string, fLoadImage: LoadImageFn) {
    const v = this._cache.get(key);
    if (v && v.url === url) {
      // console.log('found cache', { key, url });
      return v.image;
    }
    const img = await fLoadImage(url);
    this.setImage(key, url, img);
    return img;
  }

  setImage(key: string, url: string, img: GenericImage) {
    // Don't cache SVGs: they seem to render quickly on the browser, and we risk
    // caching lots of used-once images during text editing. Also, on Node, node-canvas
    // immediately rasterizes the SVG, which if the width/height specified in the SVG
    // are smaller than we need, we will cache an image that is too small, and then
    // the resulting image will be blurry when it gets expanded.
    if (url && !url.startsWith('data:image/svg+xml')) {
      this._cache.set(key, { image: img, url });
    }
  }
}

let cache = new ImageCache(0);

const getConfigBounds = (config) => {
  const ppmm = config.preview.width / config.item.width;
  
  const widthPx = config.preview.previewFile.width;
  const heightPx = config.preview.previewFile.height;

  const itemBounds = {
    top: config.preview.offsetTop,
    left: config.preview.offsetLeft || widthPx / 2 - config.preview.width / 2,
    height: config.preview.height,
    width: config.preview.width,
  };

  const designBounds = {
    top: itemBounds.top + config.print.offsetTop * ppmm,
    left: itemBounds.left + (config.preview.width - config.print.width * ppmm) / 2,
    height: config.print.height * ppmm,
    width: config.print.width * ppmm,
    ppmm,
  };

  return { itemBounds, designBounds };
};

export const CanvasContext = (getImage: GetImageFn, getScratch: GetCanvasFn, debug = false) => {
  const isBrowser = typeof document !== 'undefined';

  const elementTypeMapping = {
    title: 'font',
  };
  const baseElementType = (raw) => {
    return elementTypeMapping[raw] || raw;
  };

  const icons: { [k: string]: HTMLImageElement | { src: string } } = Object.entries({
    // name: `data:image/svg+xml,`,
    delete: `data:image/svg+xml,<svg height="8" viewBox="-4 -4 16 16" width="8" xmlns="http://www.w3.org/2000/svg"><circle cx="4" cy="4" r="8" fill="rgb(255,0,0)"/><path fill="white" d="m1.41 0-1.41 1.41.72.72 1.78 1.81-1.78 1.78-.72.69 1.41 1.44.72-.72 1.81-1.81 1.78 1.81.69.72 1.44-1.44-.72-.69-1.81-1.78 1.81-1.81.72-.72-1.44-1.41-.69.72-1.78 1.78-1.81-1.78-.72-.72z"/></svg>`,
    rotate: `data:image/svg+xml,<svg height="8" viewBox="-4 -4 16 16" width="8" xmlns="http://www.w3.org/2000/svg"><circle cx="4" cy="4" r="8" fill="rgb(51,204,255)"/><path fill="white" d="m4 0c-2.2 0-4 1.8-4 4s1.8 4 4 4c1.1 0 2.12-.43 2.84-1.16l-.72-.72c-.54.54-1.29.88-2.13.88-1.66 0-3-1.34-3-3s1.34-3 3-3c.83 0 1.55.36 2.09.91l-1.09 1.09h3v-3l-1.19 1.19c-.72-.72-1.71-1.19-2.81-1.19z"/></svg>`,
    resize: `data:image/svg+xml,<svg height="8" viewBox="-4 -4 16 16" width="8" xmlns="http://www.w3.org/2000/svg"><circle cx="4" cy="4" r="8" fill="rgb(51,204,255)"/><path fill="white" transform="rotate(90 4 4)" d="m4 0 1.66 1.66-4 4-1.66-1.66v4h4l-1.66-1.66 4-4 1.66 1.66v-4z"/></svg>`,
  }).reduce((a, [k, v]) => {
    if (isBrowser) {
      const img = document.createElement('img');
      img.src = v;
      a[k] = img;
    } else {
      a[k] = { src: v };
    }
    return a;
  }, {});

  // Ensure types match when drawing image
  const ctxDrawImage = (
    ctx: GenericCanvasCtx,
    img: GenericImage,
    ...args: [number, number, number, number]
  ) => {
    const _isNodeCxt = isNodeCanvasRenderingContext2D(ctx);
    const _isNodeImg = isNodeImage(img);
    if (_isNodeCxt && _isNodeImg) return ctx.drawImage(img, ...args);
    if (!_isNodeCxt && !_isNodeImg) return ctx.drawImage(img, ...args);
    throw new Error(
      `Draw Image mismatch: ${_isNodeCxt ? 'Node' : 'Browser'} canvas context, ${_isNodeImg ? 'Node' : 'Browser'} Image`
    );
  };

  // TODO got to be a better way to get around TS
  const ctxDrawImageFull = (
    ctx: GenericCanvasCtx,
    img: GenericImage,
    ...args: [number, number, number, number, number, number, number, number]
  ) => {
    const _isNodeCxt = isNodeCanvasRenderingContext2D(ctx);
    const _isNodeImg = isNodeImage(img);
    if (_isNodeCxt && _isNodeImg) return ctx.drawImage(img, ...args);
    if (!_isNodeCxt && !_isNodeImg) return ctx.drawImage(img, ...args);
    throw new Error(
      `Draw Image mismatch: ${_isNodeCxt ? 'Node' : 'Browser'} canvas context, ${_isNodeImg ? 'Node' : 'Browser'} Image`
    );
  };

  const renderIcon = (img, size) =>
    function (ctx, left, top, styleOverride, fabricObject) {
      ctx.save();
      ctx.translate(left, top);
      ctx.rotate(fabric.util.degreesToRadians(fabricObject.angle));
      ctx.drawImage(img, -size / 2, -size / 2, size, size);
      ctx.restore();
    };

  if (isBrowser) {
    // disable mid point control points
    delete fabric.Object.prototype.controls.mb;
    delete fabric.Object.prototype.controls.ml;
    delete fabric.Object.prototype.controls.mr;
    delete fabric.Object.prototype.controls.mt;
    delete fabric.Object.prototype.controls.mtr;
    // custom controls
    fabric.Object.prototype.cornerSize = 20;
    fabric.Object.prototype.controls.tl = new fabric.Control({
      x: -0.5,
      y: -0.5,
      //offsetY: -40,
      cursorStyle: 'crosshair',
      // @ts-expect-error - The @types/fabric seems wrong here
      actionHandler: fabric.controlsUtils.rotationWithSnapping,
      actionName: 'rotate',
      render: renderIcon(icons.rotate, 20),
      //withConnection: true,
    });
    fabric.Object.prototype.controls.br = new fabric.Control({
      x: 0.5,
      y: 0.5,
      cursorStyle: 'nwse-resize',
      // @ts-expect-error - The @types/fabric seems wrong here
      actionHandler: fabric.controlsUtils.scalingEqually,
      actionName: 'scale',
      render: renderIcon(icons.resize, 20),
    });
    fabric.Object.prototype.controls.tr = new fabric.Control({
      x: 0.5,
      y: -0.5,
      cursorStyle: 'pointer',
      // @ts-expect-error - The @types/fabric seems wrong here
      mouseUpHandler: (e, t) => {
        const target = t?.target;
        const canvas = target?.canvas;
        canvas && canvas.fire('custom:delete', { target });
      },
      actionName: 'delete',
      render: renderIcon(icons.delete, 20),
    });
    fabric.Object.prototype.lockScalingFlip = true;
  }

  const interruptibleRender = async (rawSteps, prev, callback) => {
    if (prev) prev.cancel();

    let running = false;
    let cancel = false;
    let stopped = false;

    const step = (fun) => async (callback) => {
      if (!cancel && !stopped) {
        await fun();
        callback();
      } else {
        stopped = true;
      }
    };

    const steps = rawSteps.map(step);

    const run = async (i = 0) => {
      running = true;
      setTimeout(() => {
        if (i < steps.length) {
          steps[i](() => run(i + 1));
        } else {
          stopped = true;
          callback && callback();
        }
      }, 0);
    };

    const timeout = setTimeout(async () => {
      if (prev) await prev.cancel();
      if (!cancel) {
        run();
      }
    }, 10);

    const isCancelled = () =>
      new Promise<void>((resolve) => {
        setTimeout(() => {
          if (stopped) {
            resolve();
          } else {
            isCancelled().then(resolve);
          }
        }, 0);
      });

    return {
      cancel: async () => {
        cancel = true;
        if (running && !stopped) {
          await isCancelled();
        } else {
          clearTimeout(timeout);
        }
      },
    };
  };

  const elementToUrl = (e, assetLibrary) => {
    let element = e.toJS ? e.toJS() : e;

    if (!!element.isGroup && Array.isArray(element.data)) {
      const data = element.data.find((d) => !!d.active) || element.data[0];
      element = Object.assign({}, element, data, { group: element.data });
    }
    if (!element.data) {
      element = Object.assign(element, getDefaultData(element));
    }

    return urlForCanvasElement(assetLibrary, element);
  };

  const createHTMLCanvas = (w, h) => {
    const canvas = window.document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    canvas.hidden = true;
    return canvas;
  };

  const renderToPNGUrl = async (config, graphicType, assetLibrary, elements, dispatch, dpi = 600.0, isPreview = false) => {
    const dpmm = dpi / 25.4;
    const widthMM = config.print.width;
    const heightMM = config.print.height;
    const widthPx = widthMM * dpmm;
    const heightPx = heightMM * dpmm;
    const inset = (config.print.inset || 0) * dpmm;

    const designBounds = {
      left: 0,
      top: 0,
      width: widthPx,
      height: heightPx,
      centerX: widthPx / 2,
      centerY: heightPx / 2,
      inset: {
        top: inset,
        left: inset,
        width: widthPx - inset * 2,
        height: heightPx - inset * 2,
      }
    };

    const dxfConfig = {
      itemId: config.itemId,
      face: config.face,
      item: {
        width: widthMM,
        height: heightMM,
      },
      print: {
        width: widthMM,
        height: heightMM,
      },
      preview: {
        width: widthPx,
        height: heightPx,
        offsetTop: 0.0,
        maskFile: config.preview?.maskFile,
      },
    };

    let canvas = null;
    if (isBrowser) {
      canvas = createHTMLCanvas(widthPx, heightPx);
      canvas.hidden = true;
    } else {
      canvas = createCanvas(widthPx, heightPx);
    }

    await drawElements({
      canvas,
      config: dxfConfig,
      rawConfig: config,
      assetLibrary,
      designBounds,
      elements,
      isInteractive: false,
      isPreview,
      graphicType,
    });

    return canvas.toDataURL();
  };

  const getDefaultData = (e) => {
    if (!e.isGroup && e.elementType === 'font') {
      return { data: 'Your Text Here' };
    } else {
      if (e.elementType === 'monogram') {
        return { data: 'ABC' };
      } else if (e.elementType === 'jersey') {
        return { data: 'Name', decoration: '00' };
      } else if (e.elementType === 'title') {
        return { data: 'Name', decoration: 'Title' };
      } else if (e.elementType === 'font') {
        return { data: 'Name' };
      } else {
        return null;
      }
    }
  };

  // Bitmaps need a certain miminimum DPI to look good, so we need to prevent bitmaps from being scaled too large
  const calcMaxBitmapScale = (ppmm, imgWidthPx, containerWidthPx) => {
    const ppmmMin = MINIMUM_BITMAP_DPI / 25.4;

    const maxWidthMM = imgWidthPx / ppmmMin;
    const maxWidthPx = maxWidthMM * ppmm;
    const maxScaleX = maxWidthPx / containerWidthPx;

    return maxScaleX;
  };

  const calcOffsets = (e, designBounds, ppmm, fullSizeWidth, fullSizeHeight, imgScale) => {
    const exists = (v) => typeof v !== 'undefined' && v !== null;
    if (e.get('o') === 'cc') {
      // new way
      const diff = e.has('w') ? fullSizeWidth / (e.get('w') * ppmm) : 1;
      const offsetX = exists(e.get('x'))
          ? e.get('x') * diff * ppmm + designBounds.centerX - (fullSizeWidth * imgScale) / 2
          : (designBounds.width - fullSizeWidth * imgScale) / 2;
      const offsetY = exists(e.get('y'))
          ? e.get('y') * diff * ppmm + designBounds.centerY - (fullSizeHeight * imgScale) / 2
          : (designBounds.height - fullSizeHeight * imgScale) / 2;

      return [offsetX, offsetY];
    } else {
      // old way
      const offsetX =
          designBounds.left +
          (exists(e.get('x'))
              ? e.get('x') * imgScale * fullSizeWidth
              : (designBounds.width - fullSizeWidth * imgScale) / 2);
      const offsetY =
          designBounds.top +
          (exists(e.get('y'))
              ? e.get('y') * imgScale * fullSizeHeight
              : (designBounds.height - fullSizeHeight * imgScale) / 2);

      return [offsetX, offsetY];
    }
  };

  const loadElementImageAsync = async ({
    e,
    ppmm,
    zoom,
    assetLibrary,
    designBounds,
    graphicType = 'engraved',
    isPreview = false,
  }) => {
    const id = e.get('id');
    const assetId = e.get('assetId');
    const elementType = baseElementType(e.get('elementType'));
    const asset = (assetLibrary[elementType] || []).find((e) => e.key === assetId);

    // Side effect: loads the font
    if (e.get('fontName')) {
      const font = asset;
      if (font && !font.loaded) {
        await new Promise<void>((resolve) => {
          font.load(() => resolve());
        });
      }
    }

    const elementUrl = elementToUrl(e, assetLibrary);
    const cacheKey = elementUrl.length > 200 ? sha256(elementUrl).toString() : elementUrl;
    let img = await cache.loadImage(cacheKey, elementUrl, loadImage) as HTMLImageElement;

    // only apply inset when not in preview mode
    // preview image already has it built in
    const printWidth = (!isPreview && designBounds.inset?.width) || designBounds.width;
    const printHeight = (!isPreview && designBounds.inset?.height) || designBounds.height;

    const w = isSVGImageElement(img) ? img.width.baseVal.value : img.naturalWidth || img.width;
    const h = isSVGImageElement(img) ? img.height.baseVal.value : img.naturalHeight || img.height;

    let scale = w / printWidth > h / printHeight ? printWidth / w : printHeight / h;
    if ((e.get('fixedFontSizePt') || 0) > 0) {
      // Unlike text without this tag, in which that width stays the same but the font size varies,
      // this tag indicates that the font size should stay the same. Since the size in pixels of the
      // canvas we are drawing to varies, we specify the font size in points, which is a physical
      // measurement. The text paths are always acquired at a fixed size (in pixels), so we need to
      // figure out the appropriate scale factor for this environment
      const elementScale = e.get('scale') || 1.0;
      scale = (elementScale * (e.get('fixedFontSizePt') * ptToMM * ppmm)) / FONT_RENDERING_SIZE; // FONT_RENDERING_SIZE is pixels
    }

    const fullSizeWidth = Math.trunc(w * scale * zoom);   // this is scale = 1.0
    const fullSizeHeight = Math.trunc(h * scale * zoom);  // this is scale = 1.0

    let imgScale = e.get('scale') || 1;

    // Bitmaps have a certain maximum size, otherwise the resolution will be too low
    // to print well.
    let bitmapInfo = null;
    const mimetype = e.get('mimetype');
    if (mimetype != null && mimetype !== 'image/svg+xml') {
      bitmapInfo = e.get('bitmapInfo');
      if (bitmapInfo) {
        bitmapInfo = bitmapInfo.toJS ? bitmapInfo.toJS() : bitmapInfo;
        bitmapInfo.maxScale = calcMaxBitmapScale(ppmm, bitmapInfo.width, fullSizeWidth);
        imgScale = Math.min(imgScale, bitmapInfo.maxScale);
      }
    }

    const destWidth = Math.trunc(fullSizeWidth * imgScale);
    const destHeight = Math.trunc(fullSizeHeight * imgScale);

    if (!isBrowser && (w < destWidth || h < destHeight)) {
      img = await loadImage(elementUrl, { width: destWidth, height: destHeight }) as HTMLImageElement;
      if (!isSVGImageElement(img)) {
        cache.setImage(cacheKey, elementUrl, img);
      }
    }

    let color = null;
    if (graphicType === 'color') {
      const storedColor = e.get('color')?.toLowerCase?.();
      if (!!storedColor) {
        color =
            (!isBrowser &&
                DEFAULT_COLORS.find((c) => c.display === storedColor || c.original === storedColor)?.print) ||
            storedColor ||
            '#f00';
        if (!!storedColor && storedColor !== color) {
          console.warn(`${storedColor} converted to ${color} for render.`);
        }
      }
    }

    const [offsetX, offsetY] = calcOffsets(e, designBounds, ppmm, fullSizeWidth, fullSizeHeight, imgScale);
    const rotationDeg = e.get('rotation') || 0;

    debug && console.log('element loaded', { id, isPreview, scale, zoom, imgScale, fullSizeWidth, fullSizeHeight, destWidth, destHeight, w, h, designBounds });
    return {
      asset: asset,
      image: img,
      color: color,
      left: offsetX,
      top: offsetY,
      width: destWidth,
      height: destHeight,
      fullSizeWidth: fullSizeWidth,
      fullSizeHeight: fullSizeHeight,
      imgScale: imgScale,  // scale factor of the natural size to the print size
      scale: scale,  // scale factor of the actual image to the destination size (essentially destWidth / img.width, but with better precision)
      angleRad: rotationDeg * Math.PI / 180.0,
      bitmapInfo: bitmapInfo
    };
  };

  const getDrawDesignSteps = ({
    canvas,
    config,
    rawConfig = null,
    assetLibrary,
    designBounds,
    elements,
    selectedIds,
    isInteractive,
    canEdit = true,
    graphicType = 'engraved',
    isPreview = false,
    dispatchMoveElement = (_face, _id, _x, _y, _o, _w, _h) => {},
  }) => {
    debug && console.log('getDrawDesignSteps');

    const ppmm = config.preview.width / config.item.width;
    const zoom = 1;

    const face = config.face;

    const loadText = async (e, i) => {
      true && console.log('loadText', i, e.toJS());

      if (e.get('raw')) {
        const fontSize = e.get('fixedFontSizePt') || e.get('fontSize') || 12;

        const ctext = new fabric.Textbox(e.get('data'), {
            fill: e.get('color') || '#000000',
            fontSize: fontSize,
            fontFamily: 'Helvetica,Arial,sans-serif',
        });
  
        canvas.add(ctext);
  
        ctext.set({
          left: e.get('x'),
          top: e.get('y'),
          width: e.get('w'),
        });
  
        canvas.bringToFront(ctext);
  
        console.log('raw', { ctext, canvas, ppmm, fontSize, e });
  
        return ctext;
      } else {
        const fontSize = e.get('fixedFontSizePt') || e.get('fontSize') || 12;
        const scale = ((fontSize * ptToMM * ppmm)) / FONT_RENDERING_SIZE;
  
        const [left, top] = calcOffsets(e, designBounds, ppmm, e.get('w'), e.get('w'), 1);
        const width = e.get('w') * ppmm;
  
        const ctext = new fabric.Textbox(e.get('data'), {
            fill: e.get('color') || '#000000',
            fontSize: fontSize * scale,
            fontFamily: 'Helvetica,Arial,sans-serif',
        });
  
        canvas.add(ctext);
  
        ctext.set({
          left,
          top,
          width,
          scale: e.get('scale') || 1,
        });
  
        canvas.bringToFront(ctext);
  
        console.log('cc', { ctext, canvas, ppmm, fontSize, scale, e, top, left, width });
  
        return ctext;
      }
    };

    const loadElement = async (e, i) => {
      let cloned;
      debug && console.log('loadElement', i, e.toJS());
      const id = e.get('id');
      const elementObj = await loadElementImageAsync({
        e, ppmm, zoom, assetLibrary, designBounds, graphicType, isPreview
      });

      const clipPath: any = new fabric.Rect({
        top: designBounds.top,
        left: designBounds.left,
        width: designBounds.width,
        height: designBounds.height,

        absolutePositioned: true,
      });

      // for color printing we use the mask file as an additional clipping path
      const maskFileUrl =  (rawConfig || config).preview.maskFile?.sizes?.['original'];
      // also only apply when not in preview mode
      // since again the clip path should already be built in
      if (graphicType === 'color' && maskFileUrl) {
        let mask = await cache.loadImage('_mask.' + config.face + '.' + config.itemId, maskFileUrl, loadImage);

        const maskImg = new fabric.Image(mask as HTMLImageElement);
        maskImg.set({
          absolutePositioned: true,
          top: designBounds.top,
          left: designBounds.left,
          scaleX: designBounds.width / maskImg.width,
          scaleY: designBounds.height / maskImg.height,
        });
        clipPath.set({
          clipPath: maskImg,
        });
      }

      const resizedImg = getScratch(config.itemId + '.' + config.face + '.' + id + ".resized") as HTMLCanvasElement;
      resizedImg.width = elementObj.fullSizeWidth;
      resizedImg.height = elementObj.fullSizeHeight;
      resizedImg.getContext('2d').drawImage(elementObj.image, 0, 0, resizedImg.width, resizedImg.height);
      const cimg: EnhancedFabricImage = new fabric.Image(resizedImg);
      const { width: cwidth, height: cheight } = cimg;

      cimg.set({
        left: elementObj.left,
        top: elementObj.top,
        scaleX: elementObj.imgScale,
        scaleY: elementObj.imgScale,
        lockScalingFlip: true,
        lockSkewingX: true,
        lockSkewingY: true,
        //lockScalingX: true,
        //lockScalingY: true,
        transparentCorners: true,
        //cornerStrokeColor: 'transparent',
        clipPath,
      });
      cimg.rotate(elementObj.angleRad * 180.0 / Math.PI);
      if (elementObj.bitmapInfo) {
        cimg.bitmapInfo = elementObj.bitmapInfo;
      }

      if (isInteractive && graphicType === 'engraved') {
        const mask = await cache.loadImage('_mask.' + config.face + '.' + config.itemId, config.preview.maskFile.sizes['original'], loadImage);
        const bg = new fabric.Image(mask as HTMLImageElement);
        // some masks are very large (5000x5000) which can cause pixelation in fabric clip paths
        // force the scale down to avoid this issue
        bg.set({
          scaleX: config.preview.previewFile.width / bg.width,
          scaleY: config.preview.previewFile.height / bg.height,
        });
        bg.set({
          clipPath,
        });
        const mimg = new fabric.Group([bg]);

        // Use f.u.o.clone() instead of cimg.clone(callback) because we
        // cannot control when the callback gets run. When a lot of redraws
        // happen in quick succession, such as when the user is typing
        // quickly, this can result in 'mimg' getting added to the canvas
        // *after* the canvas.renderAll() call from our caller happens,
        // which means that it can show up on the next render as an ugly
        // visual anomaly.

        cloned = fabric.util.object.clone(cimg);
        cloned.set({
          absolutePositioned: true,
        });

        mimg.clipPath = cloned;
        mimg.evented = false;
        mimg.selectable = false;

        if (!!mimg.width && !!mimg.height) {
          canvas.add(mimg);
        }

        cimg.opacity = 0;
        cimg._mask = mimg;
      } else if (graphicType === 'color' && /*!!storedColor*/elementObj.color) {
        const bg = new fabric.Rect({
          fill: elementObj.color,
          top: designBounds.top,
          left: designBounds.left,
          width: designBounds.width,
          height: designBounds.height,
        });
        bg.set({
          clipPath,
        });

        const mimg = new fabric.Group([bg]);

        cloned = fabric.util.object.clone(cimg);
        cloned.set({
          absolutePositioned: true,
        });

        mimg.clipPath = cloned;
        mimg.evented = false;
        mimg.selectable = false;

        if (!!mimg.width && !!mimg.height) {
          canvas.add(mimg);
        }

        cimg.opacity = 0;
        cimg._mask = mimg;
      }

      if (!canEdit || e.get('locked')) {
        cimg.evented = false;
        cimg.selectable = false;
      }

      const isFixed = e.get('fixed');
      const isPremium = !!elementObj.asset?.fee;
      if (isFixed || isPremium) {
        cimg.lockMovementX = true;
        cimg.lockMovementY = true;
        cimg.setControlsVisibility({
          tl: false,
          tr: !isFixed,
          bl: false,
          br: false,
        });
        cimg._isPremium = isPremium;
        cimg._isFixed = isFixed;
      }

      cimg._elementId = id;
      cimg._face = face;
      cimg._w = cwidth;
      cimg._h = cheight;
      cimg._s = elementObj.scale;
      cimg._i = config.itemId;
      cimg.snapAngle = 90;
      cimg.snapThreshold = 5;

      if (!!cimg.width && !!cimg.height && !e.get('hidden')) {
        canvas.add(cimg);
      }

      debug && console.log('element loaded', { id, scale: elementObj.scale, cwidth, cheight });
      return cimg;
    };

    const updateSelection = (selectedIds) => {
      if (selectedIds) {
        const active = canvas.getObjects().filter((o) => selectedIds.includes(o._elementId));
        if (active) {
          const hasPremium = !!active.find((o) => o._isPremium);
          const hasFixed = !!active.find((o) => o._isFixed);
          const toSelect = hasPremium
            ? active.length > 1
              ? active.filter((o) => !o._isPremium)
              : active
            : active;

          if (toSelect.length > 0) {
            const sel = new fabric.ActiveSelection(toSelect, {
              canvas: canvas.getFabricCanvas?.() || canvas,
            });
            if (hasFixed || hasPremium) {
              sel.lockMovementX = true;
              sel.lockMovementY = true;
              sel.setControlsVisibility({
                tl: false,
                tr: !hasFixed,
                bl: false,
                br: false,
              });
            }
            // TODO calculate a snap offset based on selection (make it easy to re-square element)
            sel.snapAngle = 90;
            sel.snapThreshold = 5;
            canvas.setActiveObject(sel);
          }
        }

        //if (active && active.length > 0) {
        //  const sel = new fabric.ActiveSelection(active, { canvas });
        //  canvas.setActiveObject(sel);
        //}
      }
    };

    const getObjects = () => {
      return canvas.getObjects().reduce((a, o) => {
        if (o._elementId) {
          a[o._elementId] = o;
        }
        return a;
      }, {});
    };

    const elementDrawFuncs = elements.map((e, i) => async () => {
      if (e.get('elementType') === 'proofText') {
        return loadText(e, i);
      } else {
        return await loadElement(e, i);
      }
    });

    // If an elements content changes, it changes the relative placement
    // update the stored x/y to prevent visual bouncing. However, if we just resized the canvas (e.g. ProofDesigner),
    // then we do not want to change the placement, because we already updated it in ReducerActions.
    const oldObjects = getObjects();
    const isDeferredCanvas = typeof canvas.getFabricCanvas == 'function'; // will be actual fabric canvas if rendering to PNG
    const oldConfigWidth = isDeferredCanvas ? canvas.getFabricCanvas?.()?._previewWidth : -1;
    const updatePlacement = () => {
      if (isDeferredCanvas) {
        canvas.getFabricCanvas()._previewWidth = config.preview.width;
        if (oldConfigWidth && config.preview.width !== oldConfigWidth) {
          return;
        }
      }
      const newObjects = getObjects();

      Object.keys(newObjects)
        .map(parseFloat) // note that parseInt() takes TWO parameters. But parseFloat only takes one argument, an JS doesn't have ints anyway.
        .map((id) => {
          const o = oldObjects[id];
          const n = newObjects[id];

          if (o) {
            const { _w: wo, _h: ho, _face: fo, _i: oi } = o;
            const { _w: wn, _h: hn, _face: fn, _i: ni } = n;
            const e = elements.find((e) => e.get('id') == id);

            if (e && (wo !== wn || ho !== hn) && fo === fn && oi === ni) {
              const translate = (t) => {
                const tm = t.calcTransformMatrix();
                const opt = fabric.util.qrDecompose(tm);
                const { translateX: x, translateY: y, scaleX: scale } = opt;

                return { x, y, scale, w: t.width, h: t.height };
              };

              const co = translate(o);
              const cn = translate(n);
              const cx = cn.x - co.x;
              const cy = cn.y - co.y;

            //const x = (cn.x - cx - designBounds.centerX - (cn.w * cn.scale) / 2) / (cn.scale * cn.w);
            //const y = (cn.y - cy - designBounds.centerY - (cn.h * cn.scale) / 2) / (cn.scale * cn.h);

              const x = (cn.x - cx - designBounds.centerX) / ppmm;
              const y = (cn.y - cy - designBounds.centerY) / ppmm;

              // Note: if you change this you might also need to change setFaceSize() in ReducerActions.js
              dispatchMoveElement(config.face, id, x, y, 'cc', wn / ppmm, hn / ppmm);
            }
          }
        });
    };

    return [...elementDrawFuncs, () => updateSelection(selectedIds), updatePlacement];
  };

  const drawElements = async ({
                          canvas,
                          config,
                          rawConfig = null,
                          assetLibrary,
                          designBounds,
                          elements,
                          isInteractive,
                          graphicType = 'engraved',
                          isPreview = false,
                        }) =>
  {
    debug && console.log('drawElements');

    const ppmm = config.preview.width / config.item.width;
    const zoom = 1;

    canvas.uniformScaling = true;

    const maskFileUrl =  (rawConfig || config).preview.maskFile?.sizes?.['original'];
    let maskPromise = null;
    if (maskFileUrl && ((isInteractive && graphicType === 'engraved') || graphicType == 'color')) {
      maskPromise = cache.loadImage('_mask.' + config.face + '.' + config.itemId, maskFileUrl, loadImage);  // this is likely an actual remote url, so don't await yet
    }

    const gc = canvas.getContext('2d');
    gc.clearRect(0, 0, canvas.width, canvas.height);

    // Clip to design
    gc.beginPath();
    gc.rect(designBounds.left, designBounds.top, designBounds.width, designBounds.height);
    gc.clip();

    // Draw objects
    let i = 0;
    for (const e of elements) {
      const o = await loadElementImageAsync({
        e, ppmm, zoom, assetLibrary, designBounds, graphicType, isPreview
      })
      
      if (o) {
        const destWidth = o.width;
        const destHeight = o.height;
        let img = o.image as any;

        if (o.color) {
          const scratch = getScratch(config.face + '.' + i);
          scratch.width = destWidth;
          scratch.height = destHeight;
          const ctx = scratch.getContext('2d') as GenericCanvasCtx;
          isNodeCanvasRenderingContext2D(ctx) ? (ctx.quality = 'best') : (ctx.imageSmoothingQuality = 'high');
          ctx.imageSmoothingEnabled = true;

          const ctx2d = ctx as any;
          ctx2d.drawImage(o.image, 0, 0, scratch.width, scratch.height);
          ctx2d.globalCompositeOperation = 'source-in';
          ctx2d.fillStyle = o.color;
          ctx2d.fillRect(0, 0, scratch.width, scratch.height);

          img = scratch;
        }

        gc.save();
        // The (left,top) coordinate works with fabric, so we need to emulate them
        gc.translate(o.left + destWidth / 2, o.top + destHeight / 2);
        gc.rotate(o.angleRad);
        gc.translate(-0.5 * destWidth, -0.5 * destHeight);
        gc.drawImage(img, 0, 0, destWidth, destHeight);
        gc.restore();
      }
      i += 1;
    }

    // Handle masking
    if (maskPromise) {
      let maskImg = await maskPromise;
      gc.save();
      if (graphicType == "engraved") {
        // The mask for engraved designs is actually a texture which replaces the design pixels
        gc.globalCompositeOperation = "source-atop";
        gc.drawImage(maskImg,
                     designBounds.left + 0.5 * designBounds.width - 0.5 * maskImg.width,
                     designBounds.top + 0.5 * designBounds.height - 0.5 * maskImg.height);
      } else {
        // The mask for non-engraved designs (currently, color) is a clip mask
        gc.globalCompositeOperation = "destination-in";
        gc.drawImage(maskImg, designBounds.left, designBounds.top, designBounds.width, designBounds.height);
      }
      gc.restore();
    }
  };

  const loadImage = async (src: string, minimumSize: MinimumSize = undefined): Promise<GenericImage> => {
    const img = await getImage(src);
    if (
      !isSVGImageElement(img) &&
      img.naturalWidth &&
      img.naturalHeight &&
      (!minimumSize ||
        isBrowser ||
        (img.naturalWidth >= minimumSize.width && img.naturalHeight >= minimumSize.height))
    ) {
      return img;
    } else {
      return fixSvgIfPossible(src, img, minimumSize);
    }
  };

  const fixSvgIfPossible = async (
    src: string,
    originalImage: GenericImage,
    minimumSize: MinimumSize | undefined
  ): Promise<GenericImage> => {
    if (isBrowser) {
      return fixSvgIfPossible_browser(src, originalImage);
    } else {
      return fixSvgIfPossible_node(src, originalImage, minimumSize);
    }
  };

  const fixSvgIfPossible_browser = async (
    src: string,
    originalImage: GenericImage
  ): Promise<GenericImage> => {
    return new Promise((resolve, reject) => {
      const emptyImg = getImage();

      if (src) {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', src, true);
        xhr.onreadystatechange = () => {
          if (xhr.readyState === XMLHttpRequest.DONE) {
            const status = xhr.status;
            if (status === 0 || (status >= 200 && status < 400)) {
              const contentType = xhr.getResponseHeader('Content-Type');
              if (contentType.match(/svg/)) {
                const xml = xhr.responseXML;
                const svg = xml.documentElement;
                const viewbox = svg.getAttribute('viewBox') || svg.getAttribute('viewbox');
                const [x0, y0, x1, y1] = viewbox.split(/\s+/).map(parseFloat);
                const w = x1 - x0;
                const h = y1 - y0;
                const width = svg.getAttribute('width');
                const height = svg.getAttribute('height');
                if (!width) {
                  svg.setAttribute('width', w.toString());
                }
                if (!height) {
                  svg.setAttribute('height', h.toString());
                }
                const fixed = new XMLSerializer().serializeToString(svg);
                const svgBase64 = btoa(fixed);
                const imageURI = 'data:image/svg+xml;base64,' + svgBase64;
                resolve(getImage(imageURI));
              } else {
                resolve(originalImage);
              }
            } else {
              resolve(emptyImg);
            }
          }
        };
        xhr.onerror = (e) => {
          reject(`Could not load image src '${src}' (error: ${e})`);
        };
        xhr.send();
      } else {
        resolve(emptyImg);
      }
    });
  };

  const fixSvgIfPossible_node = async (
    src: string,
    originalImage: GenericImage,
    minimumSize: MinimumSize | undefined
  ): Promise<GenericImage> => {
    // This function has two goals. If the SVG has no width/height attributes, we need to get them from
    // the viewbox. But, node-canvas' Image object (NCImage in this code) renders the SVG output to
    // a bitmap of width/height at the time of creation, which means that if we need to scale this up,
    // we will get blurry edges. (The browser does not render the SVG until it knows the final size, so everything
    // is always crisp.) So the other thing that we do is make the width/height a multiple of the
    // viewbox size, based on the minimumSize parameter. This overrides addition of width/height
    // attributes, but it also requires the use of a viewBox attribute, because the viewBox defines
    // the coordinate edges, and width/height the number of pixels in size. We want to increase the
    // number of pixels rendered to, but not change the view onto the paths.
    return new Promise((resolve, _reject) => {
      if (src.startsWith('data:image/svg')) {
        const dataStartIdx = src.indexOf(',') + 1;
        let svg = Buffer.from(src.substring(dataStartIdx), 'base64').toString();
        // Note: SVG tags and attributes are case-sensitive
        const tagStartIdx = svg.indexOf('<svg'); // can't use '<svg ', might be '<svg\n' or '<svg\r\n'
        const tagEndIdx = svg.indexOf('>', tagStartIdx);
        const tag = svg.substring(tagStartIdx, tagEndIdx);
        const attrs = new Map();
        const regex = /([a-zA-Z:]+) *= *"([^"]*)"/dg; // need to include colon, might be 'xmlns:svg', etc.
        let rxResults;
        while ((rxResults = regex.exec(tag)) !== null) {
          attrs[rxResults[1]] = rxResults[2]; // [0] is entire regex, [1] is group 1, [2] is group 2
        }

        const viewBox = attrs['viewBox'];
        if (viewBox || (attrs['width'] && attrs['height'])) {
          let vbWidth = 0;
          let vbHeight = 0;
          if (viewBox) {
            const [x, y, w, h] = viewBox.split(/\s+/).map(parseFloat);
            vbWidth = w;
            vbHeight = h;
          } else {
            vbWidth = parseFloat(attrs['width']);
            vbHeight = parseFloat(attrs['height']);
          }
          let w = vbWidth;
          let h = vbHeight;
          if (minimumSize && (w < minimumSize.width || h < minimumSize.height)) {
            const scale = Math.max(minimumSize.width / w, minimumSize.height / h);
            w *= scale;
            h *= scale;
          }
          // librsvg fails if dimensions are greater than 32k
          if (w > 32000 || h > 32000) {
            const scale = 32000 / Math.max(w, h);
            w *= scale;
            h *= scale;
          }
          attrs['width'] = w.toString();
          attrs['height'] = h.toString();
          if (!viewBox) {
            attrs['viewBox'] = `0 0 ${w} ${h}`;
          }

          let newTag = '';
          for (const k in attrs) {
            newTag = newTag + `${k}="${attrs[k]}" `; // note space at end
          }
          svg = svg.substring(0, tagStartIdx) + '<svg ' + newTag + svg.substring(tagEndIdx);
          return resolve(getImage('data:image/svg+xml;base64,' + Buffer.from(svg).toString('base64')));
        }
      }
      return resolve(originalImage);
    });
  };

  const getDrawProductSteps = ({
    canvas,
    width: maxWidth,
    height: maxHeight,
    imageSize = 'original',
    config,
    assetLibrary,
    designBounds,
    grid,
    elements,
    selected,
    isZoomed = false,
    isInteractive = true,
    canEdit = true,
    graphicType = 'engraved',
    mode,
    units,
    debug,
    isPreview = false,
    dispatchPositionElement = (_face, _id, _x, _y, _o, _w, _h) => {},
    onBackgroundLoaded,
  }) => {
    debug && console.log('getDrawProductSteps', { itemId: config.itemId, face: config.face });

    const drawingSteps = getDrawDesignSteps({
      canvas,
      config,
      assetLibrary,
      designBounds,
      elements,
      selectedIds: selected,
      isInteractive,
      canEdit,
      graphicType,
      isPreview,
      dispatchMoveElement: dispatchPositionElement,
    });

    const initCanvas = () => {
      canvas.uniformScaling = true;
    };

    const loadBackground = async () => {
      const url = config.preview.previewFile.sizes[imageSize];
      // debug && console.log('loadBackground', url);
      const bg = await cache.loadImage('_previewBG.' + config.face + '.' + config.itemId, url, loadImage);

      onBackgroundLoaded && onBackgroundLoaded(bg);

      const bgImg = new fabric.Image(bg as HTMLImageElement);

      const [bgW, bgH] = isSVGImageElement(bg) ? [bg.width.baseVal.value, bg.height.baseVal.value] : [bg.width, bg.height];

      const [newWidth, newHeight] = (bgW < maxWidth && bgH < maxHeight)
        ? [bgW, bgH]
        : bgW / maxWidth > bgH / maxHeight
          ? [maxWidth, bgH * maxWidth / bgW]
          : [bgW * maxHeight / bgH, maxHeight];

      const cW = newWidth; // canvas.getWidth();
      const cH = newHeight; // canvas.getHeight();

      const bounds = isZoomed ? designBounds : { left: 0, top: 0, width: bg.width, height: bg.height };
      const padding = bounds.width * (isZoomed ? 0.1 : 0);

      const ppmm = config.preview.width / config.item.width;
      const ppin = ppmm * 25.4;
      const tickSize = 60;

      const vW = bounds.width + padding + (mode === 'pro' ? tickSize : 0);
      const vH = bounds.height + padding + (mode === 'pro' ? tickSize : 0);

      const z = Math.min(cW / vW, cH / vH);

      canvas.setZoom(z);
      canvas.absolutePan({
        x: (bounds.left - padding / 2) * z - (cW - vW * z) / 2,
        y: (bounds.top - padding / 2) * z - (cH - vH * z) / 2,
      });

      bgImg.selectable = false;
      bgImg.evented = false;
      bgImg.globalCompositeOperation = 'destination-over';
      canvas.clear();
      console.log('setDimensions', { newWidth, newHeight, bgImg, bg })
      canvas.setDimensions({ width: cW, height: cH });
      canvas.add(bgImg);

      if (canEdit) {
        // dashed box
        const strokeWidth = 1 / z;
        const strokeDash = strokeWidth * 2;
        const strokeDashArray = [strokeDash, strokeDash * 2];
        const strokeDashOffset = strokeDash / 2;
        const strokeOpacity = 0.3;
        const bounds = {
          top: designBounds.top - strokeWidth,
          height: designBounds.height + strokeWidth,
          left: designBounds.left - strokeWidth,
          width: designBounds.width + strokeWidth,
          fill: 'transparent',
        };
        [
          {
            stroke: '#000',
            strokeWidth,
            strokeDashArray,
            strokeDashOffset,
            strokeOpacity,
            strokeLineCap: 'square',
          },
          { stroke: '#fff', strokeWidth, strokeOpacity },
        ].forEach((stroke) => {
          const designRect = new fabric.Rect(Object.assign({}, bounds, stroke));
          designRect.selectable = false;
          designRect.evented = false;
          canvas.add(designRect);

          canvas.sendToBack(designRect);
        });

        const gridOpts = {
          stroke: '#3cf',
          strokeOpacity: 0.5,
        };
        grid &&
          grid.x.forEach((x) => {
            const line = new fabric.Line(
              [x, designBounds.top, x, designBounds.top + designBounds.height],
              gridOpts
            );
            canvas.add(line);
            line.selectable = false;
            line.evented = false;
            canvas.sendToBack(line);
          });
        grid &&
          grid.y.forEach((y) => {
            const line = new fabric.Line(
              [designBounds.left, y, designBounds.left + designBounds.width, y],
              gridOpts
            );
            canvas.add(line);
            line.selectable = false;
            line.evented = false;
            canvas.sendToBack(line);
          });

        if (mode === 'pro' && grid) {
          // const p = ppmm * 1.5;
          const p = 30;

          const tickOpts = {
            stroke: '#fff',
            //strokeWidth: Math.ceil(ppmm / 10),
            strokeWidth: 1,
          };

          const fontOpts = {
            fontSize: isZoomed ? 16 : 20,
            fontFamily: 'Arial',
            fill: '#fff',
            selectable: false,
            evented: false,
            textAlign: 'center',
            originX: 'center',
            originY: 'center',
          };

          console.log({ ppmm, isZoomed, bounds, z, fontOpts });

          if (units === 'in') {
            const ticksY = Math.ceil(designBounds.height / 2.0 / ppin * 16);
            for (let i = 0; i <= ticksY; i++) {
              const x = designBounds.left + designBounds.width;
              [-i, i].forEach((i) => {
                const y = designBounds.centerY + i * ppin / 16;
                const w = tickSize * (i % 16 === 0 ? 0.5 : i % 8 === 0 ? 0.4 : i % 4 === 0 ? 0.25 : 0.1);
                const tick = new fabric.Line([x + 5, y, x + 5 + w, y], tickOpts);
                canvas.add(tick);
                tick.selectable = false;
                tick.evented = false;
                canvas.sendToBack(tick);

                if (i % 16 === 0) {
                  const text = new fabric.Text(Math.abs(i / 16).toString(), {
                    left: x + tickSize + 5,
                    top: y,
                    ...fontOpts,
                  });
                  canvas.add(text);
                }
              });
            }
  
            const ticksX = Math.ceil(designBounds.width / 2.0 / ppin * 16);
            for (let i = 0; i <= ticksX; i++) {
              const y = designBounds.top + designBounds.height;
              [-i, i].forEach((i) => {
                const x = designBounds.centerX + i * ppin / 16;
                const w = tickSize * (i % 16 === 0 ? 0.5 : i % 8 === 0 ? 0.4 : i % 4 === 0 ? 0.25 : 0.15);
                const tick = new fabric.Line([x, y + 5, x, y + 5 + w], tickOpts);
                canvas.add(tick);
                tick.selectable = false;
                tick.evented = false;
                canvas.sendToBack(tick);
  
                if (i % 16 === 0) {
                  const text = new fabric.Text(Math.abs(i / 16).toString(), {
                    left: x,
                    top: y + tickSize + 5,
                    ...fontOpts,
                  });
                  canvas.add(text);
                }
              });
            }
          } else {
            const ticksY = Math.ceil(designBounds.height / 2.0 / ppmm);
            for (let i = 0; i <= ticksY; i++) {
              const x = designBounds.left + designBounds.width;
              [-i, i].forEach((i) => {
                const y = designBounds.centerY + i * ppmm;
                const w = tickSize * (i % 10 === 0 ? 0.5 : i % 5 === 0 ? 0.4 : 0.25);
                const tick = new fabric.Line([x + p, y, x + p + w, y], tickOpts);
                canvas.add(tick);
                tick.selectable = false;
                tick.evented = false;
                canvas.sendToBack(tick);
  
                if (i % 10 === 0) {
                  const text = new fabric.Text(Math.abs(i).toString(), {
                    left: x + p + tickSize * 0.6,
                    top: y - (3 * ppmm) / 2,
                    fontSize: 3 * ppmm,
                    fontFamily: 'Arial',
                    fill: '#fff',
                    selectable: false,
                    evented: false,
                  });
                  canvas.add(text);
                }
              });
            }
  
            const ticksX = Math.ceil(designBounds.width / 2.0 / ppmm);
            for (let i = 0; i <= ticksX; i++) {
              const y = designBounds.top + designBounds.height;
              [-i, i].forEach((i) => {
                const x = designBounds.centerX + i * ppmm;
                const w = tickSize * (i % 10 === 0 ? 0.5 : i % 5 === 0 ? 0.4 : 0.25);
                const tick = new fabric.Line([x, y + p, x, y + p + w], tickOpts);
                canvas.add(tick);
                tick.selectable = false;
                tick.evented = false;
                canvas.sendToBack(tick);
  
                if (i % 10 === 0) {
                  const text = new fabric.Text(Math.abs(i).toString(), {
                    left: x - (3 * ppmm) / 2,
                    top: y + p + tickSize * 0.6,
                    fontSize: 3 * ppmm,
                    fontFamily: 'Arial',
                    fill: '#fff',
                    selectable: false,
                    evented: false,
                  });
                  canvas.add(text);
                }
              });
            }
          }

          const poly = new fabric.Polygon(
            [
              { x: designBounds.left - p * 2, y: designBounds.top + designBounds.height },
              { x: designBounds.left + designBounds.width, y: designBounds.top + designBounds.height },
              { x: designBounds.left + designBounds.width, y: designBounds.top - p * 2 },
              { x: designBounds.left + designBounds.width + tickSize + p * 2, y: designBounds.top - p * 2 },
              {
                x: designBounds.left + designBounds.width + tickSize + p * 2,
                y: designBounds.top + designBounds.height + tickSize + p * 2,
              },
              { x: designBounds.left - p * 2, y: designBounds.top + designBounds.height + tickSize + p * 2 },
            ],
            {
              fill: '#000',
              opacity: 0.7,
              selectable: false,
              evented: false,
            }
          );
          canvas.add(poly);
          canvas.sendToBack(poly);
        }

        // mask
        /*
        const opacity = 0.1;
        const top = new fabric.Rect({
          width,
          height: designBounds.top,
          top: 0,
          left: 0,
          opacity,
          fill: '#000',
          selectable: false,
          evented: false,
        });
        const left = new fabric.Rect({
          width: designBounds.left,
          height: height - designBounds.top,
          top: designBounds.top,
          left: 0,
          opacity,
          fill: '#000',
          selectable: false,
          evented: false,
        });
        const right = new fabric.Rect({
          width: width - designBounds.left - designBounds.width,
          height: height - designBounds.top,
          top: designBounds.top,
          left: designBounds.left + designBounds.width,
          opacity,
          fill: '#000',
          selectable: false,
          evented: false,
        });
        const bottom = new fabric.Rect({
          width: designBounds.width,
          height,
          top: designBounds.top + designBounds.height,
          left: designBounds.left,
          opacity,
          fill: '#000',
          selectable: false,
          evented: false,
        });

        canvas.add(top);
        canvas.add(left);
        canvas.add(right);
        canvas.add(bottom);
        */
      }

      canvas.sendToBack(bgImg);
    };

    const applyCorners = async () => {
      if (config.preview.corners) {
        const scratch = getScratch(config.face + '.corners');
        scratch.width = designBounds.width;
        scratch.height = designBounds.height;
        const ctx = scratch.getContext('2d') as GenericCanvasCtx;
        isNodeCanvasRenderingContext2D(ctx) ? (ctx.quality = 'best') : (ctx.imageSmoothingQuality = 'high');
        ctx.imageSmoothingEnabled = true;

        const printWidth = designBounds.width;
        const printHeight = designBounds.height;

        const ppmm = config.preview.width / config.item.width;

        const itemWidth = config.item.width * ppmm;
        const itemHeight = config.item.height * ppmm;
        scratch.width = itemWidth;
        scratch.height = itemHeight;

        ctx.clearRect(0, 0, scratch.width, scratch.height);

        const left = config.print.offsetLeft ? config.print.offsetLeft * ppmm : (itemWidth - printWidth) / 2;
        const top = (config.print.offsetTop || 0) * ppmm;

        const designUrl = await renderToPNGUrl(config, graphicType, assetLibrary, elements, () => {}, 50.0, isPreview);
        const design = await loadImage(designUrl);
        
        ctxDrawImage(ctx, design, left, top, printWidth, printHeight);
        const matted = await loadImage(scratch.toDataURL());

        scratch.width = config.preview.previewFile.width;
        scratch.height = config.preview.previewFile.height;
        ctx.clearRect(0, 0, scratch.width, scratch.height);

        drawPerspective(
          ctx, // draw to
          matted,
          0,
          0,
          itemWidth,
          itemHeight, // sample from
          config.preview.corners.map((c) => c[0]), // x
          config.preview.corners.map((c) => c[1]), // y
          5, // divisions
          5 // smoothness
        );

        const warped = await loadImage(scratch.toDataURL());
        const wimg = new fabric.Image(warped as HTMLImageElement);
        wimg.evented = false;
        wimg.selectable = false;
        canvas.add(wimg);
      }
    };

    const steps = [
      () => initCanvas(),
      async () => await loadBackground(),
      ...drawingSteps,
      async () => await applyCorners(),
      () => canvas.renderAll(),
    ];

    return steps;
  };

  const renderProductToPNGUrl = async ({ config, ...args }) => {
    const ppmm = config.preview.width / config.item.width;

    const widthPx = config.preview.previewFile.width;
    const heightPx = config.preview.previewFile.height;

    const itemBounds = {
      top: config.preview.offsetTop,
      left: config.preview.offsetLeft || widthPx / 2 - config.preview.width / 2,
      height: config.preview.height,
      width: config.preview.width,
    };

    const _designBounds = {
      top: itemBounds.top + config.print.offsetTop * ppmm,
      left: itemBounds.left + (config.preview.width - config.print.width * ppmm) / 2,
      height: config.print.height * ppmm,
      width: config.print.width * ppmm,
      ppmm,
    };

    const inset = (config.print.inset || 0) * ppmm;

    const designBounds = Object.assign({}, _designBounds, {
      centerX: _designBounds.left + _designBounds.width / 2,
      centerY: _designBounds.top + _designBounds.height / 2,
      inset: {
        top: inset,
        left: inset,
        width: widthPx - inset * 2,
        height: heightPx - inset * 2,
      }
    });

    let canvas = null;
    let c;
    if (isBrowser) {
      canvas = createHTMLCanvas(widthPx, heightPx);
      canvas.hidden = true;
      c = new fabric.Canvas(canvas, { enableRetinaScaling: false });
    } else {
      c = new fabric.Canvas(null, { enableRetinaScaling: false, width: widthPx, height: heightPx });
    }

    c.clear();
    c.uniformScaling = true;

    // @ts-expect-error - Bit of a pain to get the function signatures configured here
    const steps = getDrawProductSteps({
      canvas: c,
      config,
      designBounds,
      width: widthPx,
      height: heightPx,
      canEdit: false,
      isPreview: true,
      ...args,
    });

    for (const step of steps) {
      debug && console.log('step', step);
      await step();
    }

    c.renderAll();

    const url = c.toDataURL({ format: 'png' });

    if (canvas) {
      canvas.remove();
    }

    return url;
  };


  const colorizeImage = async (e: any, color = '#000') => {
    const isUrl = typeof e === 'string';

    const img = isUrl ? await loadImage(e) : e;

    const colorized = getScratch('colorizeImage') as HTMLCanvasElement;
    colorized.width = img.naturalWidth;
    colorized.height = img.naturalHeight;

    const context = colorized.getContext('2d')
    context.save();
    context.drawImage(img, 0, 0, colorized.width, colorized.height);
    context.globalCompositeOperation = "source-in";
    context.fillStyle = color;
    context.fillRect(0, 0, colorized.width, colorized.height);
    context.restore();

    const url = colorized.toDataURL('image/png');

    // give back what we got in
    return isUrl ? url : await getImage(url);
  };

  const resizeImage = async (e: any, maxWidth: number, maxHeight: number) => {
    const isUrl = typeof e === 'string';

    const img = isUrl ? await loadImage(e) : e;

    const [ width, height ] = (() => {
      if (img.naturalWidth > img.naturalHeight || !maxHeight) {
        const r = maxWidth / img.naturalWidth;
        return [maxWidth, img.naturalHeight * r];
      } else {
        const r = maxWidth / img.naturalWidth;
        return [img.naturalWidth * r, maxHeight];
      }
    })();

    const resized = getScratch('resizeImage') as HTMLCanvasElement;

    resized.width = width;
    resized.height = height;

    const context = resized.getContext('2d')
    context.drawImage(img, 0, 0, resized.width, resized.height);

    const url = resized.toDataURL('image/png');

    // give back what we got in
    return isUrl ? url : await getImage(url);
  };

  return {
    baseElementType,
    interruptibleRender,
    elementToUrl,
    renderToPNGUrl,
    getDrawProductSteps,
    renderProductToPNGUrl,
    loadImage,
    colorizeImage,
    resizeImage,
  };
};

export const BrowserCanvasContext = CanvasContext(
  (src) =>
    new Promise((resolve, reject) => {
      const img = document.createElement('img');
      img.crossOrigin = 'Anonymous';

      img.onload = () => {
        resolve(img);
      };
      img.onerror = (e) => {
        reject(`Could not load image src '${src}' (error: ${e})`);
      };
      img.src = src;
    }),
  (id) => {
    const found = document.getElementById('scratch.' + id);
    if (found) {
      return found as HTMLCanvasElement;
    } else {
      const wrapper = document.createElement('div');
      wrapper.style.height = '0';
      wrapper.style.overflow = 'hidden';
      const created = document.createElement('canvas');
      created.id = 'scratch.' + id;
      created.style.visibility = 'hidden';
      wrapper.append(created);
      document.body.append(wrapper);
      return created;
    }
  }
);
